import React from 'react'
import { createPortal } from 'react-dom'

export type PopoverProps = {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  trigger: (props: { ref: React.Ref<any>; onClick: () => void; onMouseEnter: () => void; onMouseLeave: () => void; 'aria-expanded': boolean; }) => React.ReactNode
  children: React.ReactNode
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  className?: string
}

// Lightweight popover rendered in a portal with fixed positioning.
// Opens on click only and closes on outside click or Escape.
const Popover: React.FC<PopoverProps> = ({
  open: controlledOpen,
  defaultOpen,
  onOpenChange,
  trigger,
  children,
  align = 'start',
  side = 'right',
  className,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(!!defaultOpen)
  const isControlled = typeof controlledOpen === 'boolean'
  const open = isControlled ? (controlledOpen as boolean) : uncontrolledOpen
  const setOpen = (v: boolean) => {
    if (!isControlled) setUncontrolledOpen(v)
    onOpenChange?.(v)
  }
  const btnRef = React.useRef<HTMLButtonElement | null>(null)
  const panelRef = React.useRef<HTMLDivElement | null>(null)
  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null)

  React.useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node
      if (panelRef.current?.contains(t) || btnRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDocClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDocClick)
    }
  }, [open])

  // Compute fixed coordinates when opened and on resize/scroll
  const computePosition = React.useCallback(() => {
    const btn = btnRef.current
    const panel = panelRef.current
    if (!btn) return
    const r = btn.getBoundingClientRect()
    const gap = 8
    let top = r.top
    let left = r.left
    const panelH = panel?.offsetHeight ?? 0
    const panelW = panel?.offsetWidth ?? 0
    if (side === 'right') {
      left = r.right + gap
      if (align === 'start') top = r.top
      else if (align === 'center') top = r.top + r.height / 2 - panelH / 2
      else top = r.bottom - panelH
    } else if (side === 'left') {
      left = r.left - gap - panelW
      if (align === 'start') top = r.top
      else if (align === 'center') top = r.top + r.height / 2 - panelH / 2
      else top = r.bottom - panelH
    } else if (side === 'top') {
      top = r.top - gap - panelH
      if (align === 'start') left = r.left
      else if (align === 'center') left = r.left + r.width / 2 - panelW / 2
      else left = r.right - panelW
    } else {
      // bottom
      top = r.bottom + gap
      if (align === 'start') left = r.left
      else if (align === 'center') left = r.left + r.width / 2 - panelW / 2
      else left = r.right - panelW
    }
    // Constrain to viewport a bit
    const vw = window.innerWidth
    const vh = window.innerHeight
    top = Math.max(8, Math.min(vh - (panelH || 0) - 8, top))
    left = Math.max(8, Math.min(vw - (panelW || 0) - 8, left))
    setCoords({ top, left })
  }, [align, side])

  React.useLayoutEffect(() => {
    if (!open) return
    // measure after render
    const id = requestAnimationFrame(computePosition)
    const onWin = () => computePosition()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      cancelAnimationFrame(id)
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open, computePosition])

  return (
    <div className="relative inline-block">
      {trigger({
        ref: btnRef as any,
        onClick: () => setOpen(!open),
        onMouseEnter: () => { },
        onMouseLeave: () => { },
        'aria-expanded': open,
      })}
      {open && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          style={{ position: 'fixed', top: coords?.top ?? -9999, left: coords?.left ?? -9999, visibility: coords ? 'visible' : 'hidden' }}
          className={[
            'z-50 min-w-64 max-w-sm rounded-xl border border-black/5 bg-white/95 backdrop-blur-sm shadow-xl ring-1 ring-black/5 p-3 text-sm',
            className || '',
          ].join(' ')}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  )
}

export default Popover
